import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import FileUpload from "./FileUpload";
import { api, get, put } from "../../../utils/api";
import ActionButtons from "../../../Components/Quetionary/InitialKickoff/MaterialTopics/ActionButtons";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GovernanceLayout from "../../../Layout/GovernanceLayout";

const policyFields = [
  { key: "businessEthicsPolicy", label: "Business Ethics Policy" },
  { key: "environmentalPolicy", label: "Environmental Policy" },
  { key: "supplierCodeOfConductPolicy", label: "Supplier code of conduct Policy" },
  { key: "sustainableProcurementPolicy", label: "Sustainable procurement Policy" },
  { key: "antibrieberyAndCorruptionPolicy", label: "Antibriebery and Corruption policy" },
  {
    key: "policyOnCyberSecurityAndRisksRelatedToDataPrivacy",
    label: "Policy on cyber security and risks related to data privacy?",
  },
];

const emptyPolicy = {
  status: true,
  isRevised: false,
  documentUrl: "",
  remark: "",
  effectiveDate: "",
};
const hasUploadedDocument = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") {
    const cleaned = value.trim();
    if (!cleaned) return false;
    if (cleaned.toLowerCase() === "no documents available") return false;
    return true;
  }
  if (value && typeof value === "object") return Object.keys(value).length > 0;
  return Boolean(value);
};

const sanitizeDocumentUrl = (value) => {
  if (typeof value !== "string") return value || "";
  const cleaned = value.trim();
  if (!cleaned) return "";
  if (cleaned.toLowerCase() === "no documents available") return "";
  return cleaned;
};

const normalizePolicy = (policy = {}) => {
  const normalizedStatus =
    typeof policy?.status === "boolean"
      ? policy.status
      : true;
  const normalizedIsRevised =
    typeof policy?.isRevised === "boolean"
      ? policy.isRevised
      : false;

  return {
    ...emptyPolicy,
    ...policy,
    status: normalizedStatus,
    isRevised: normalizedIsRevised,
    documentUrl: sanitizeDocumentUrl(policy?.documentUrl),
  };
};

const isPolicyEmptyForPrefill = (policy = {}) => {
  const normalized = normalizePolicy(policy);
  return (
    !hasUploadedDocument(normalized.documentUrl) &&
    !String(normalized.remark || "").trim() &&
    !String(normalized.effectiveDate || "").trim()
  );
};

/** YYYY-MM-DD bounds for effective date: within reporting year; if that year is the current calendar year, cap at today. */
const getEffectiveDateMinMax = (reportingYear) => {
  if (!Number.isFinite(reportingYear) || reportingYear <= 0) return { min: undefined, max: undefined };
  const pad = (n) => String(n).padStart(2, "0");
  const min = `${reportingYear - 1}-01-01`;
  const now = new Date();
  const currentYear = now.getFullYear();
  const max =
    reportingYear === currentYear
      ? `${currentYear}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
      : `${reportingYear}-12-31`;
  return { min, max };
};

const Policies = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [moduleType, setModuleType] = useState("");
  const [policies, setPolicies] = useState({
    businessEthicsPolicy: { ...emptyPolicy },
    environmentalPolicy: { ...emptyPolicy },
    supplierCodeOfConductPolicy: { ...emptyPolicy },
    sustainableProcurementPolicy: { ...emptyPolicy },
    antibrieberyAndCorruptionPolicy: { ...emptyPolicy },
    policyOnCyberSecurityAndRisksRelatedToDataPrivacy: { ...emptyPolicy },
  });
  const [errors, setErrors] = useState({});
  const [previousPoliciesState, setPreviousPoliciesState] = useState({});
  const [reportingYear, setReportingYear] = useState(null);
  const [isFirstEntry, setIsFirstEntry] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`${api}/governance/getGovernance?id=${id}`);
        const record = response.data?.data?.[0] || {};
        const data = response.data?.data?.[0]?.policies || {};

        const rawModule = (
          record?.module ||
          record?._governanceDataRef?.module ||
          ""
        )
          .toString()
          .toLowerCase()
          .trim();
        let resolvedModuleType = "";
        if (rawModule.includes("quarter")) resolvedModuleType = "quarterly";
        else if (rawModule.includes("annual") || rawModule.includes("month"))
          resolvedModuleType = "annual";
        setModuleType(resolvedModuleType);

        let previousPolicies = {};
        if (resolvedModuleType === "annual") {
          const year =
            Number(record?._governanceDataRef?.yearToSubmit) ||
            Number(record?.yearToSubmit) ||
            Number(record?.year) ||
            new Date().getFullYear();
          setReportingYear(year);
          const companyId = record?.company;
          if (year && companyId) {
            try {
              const prevRes = await get(
                `/governance/annual/previous-policies?year=${year}&companyId=${companyId}`
              );
              const prevData = prevRes?.data?.data || {};
              setIsFirstEntry(Boolean(prevData?.isFirstEntry));
              previousPolicies = prevData?.policies || {};
              setPreviousPoliciesState(previousPolicies || {});
            } catch (prevErr) {
              console.warn("Previous policies fetch failed:", prevErr);
              setPreviousPoliciesState({});
              setIsFirstEntry(false);
            }
          }
        } else {
          setPreviousPoliciesState({});
          setReportingYear(null);
          setIsFirstEntry(false);
        }

        const mergeCurrentWithPrevious = (key) => {
          const currentPolicy = normalizePolicy(data[key]);
          const previousPolicy = normalizePolicy(previousPolicies[key]);
          const hasPreviousValue = !isPolicyEmptyForPrefill(previousPolicy);
          // Use previous policy only when it actually has meaningful values.
          // If previous is empty/not found, keep current DB values.
          return isPolicyEmptyForPrefill(currentPolicy) && hasPreviousValue
            ? previousPolicy
            : currentPolicy;
        };

        setPolicies({
          businessEthicsPolicy: mergeCurrentWithPrevious("businessEthicsPolicy"),
          environmentalPolicy: mergeCurrentWithPrevious("environmentalPolicy"),
          supplierCodeOfConductPolicy: mergeCurrentWithPrevious(
            "supplierCodeOfConductPolicy"
          ),
          sustainableProcurementPolicy: mergeCurrentWithPrevious(
            "sustainableProcurementPolicy"
          ),
          antibrieberyAndCorruptionPolicy: mergeCurrentWithPrevious(
            "antibrieberyAndCorruptionPolicy"
          ),
          policyOnCyberSecurityAndRisksRelatedToDataPrivacy: mergeCurrentWithPrevious(
            "policyOnCyberSecurityAndRisksRelatedToDataPrivacy"
          ),
        });
      } catch (error) {
        console.error("Error fetching data:", error.response || error);
      }
    };
    fetchData();
  }, []);

  const handlePolicyChange = (policyKey, field, value) => {
    const shouldResetPolicyFields = field === "isRevised" && value === true;
    setPolicies((prev) => ({
      ...prev,
      [policyKey]: {
        ...prev[policyKey],
        [field]: value,
        ...(shouldResetPolicyFields
          ? {
              documentUrl: "",
              remark: "",
              effectiveDate: "",
            }
          : {}),
      },
    }));

    if (field === "status" && value === false) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[policyKey];
        delete newErrors[`${policyKey}_remark`];
        delete newErrors[`${policyKey}_effectiveDate`];
        return newErrors;
      });
    }

    if (field === "documentUrl" && hasUploadedDocument(value)) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[policyKey];
        return newErrors;
      });
    }
    if (field === "remark" && String(value || "").trim()) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[`${policyKey}_remark`];
        return newErrors;
      });
    }
    if (field === "effectiveDate" && String(value || "").trim()) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[`${policyKey}_effectiveDate`];
        return newErrors;
      });
    }

    if (shouldResetPolicyFields) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[policyKey];
        delete newErrors[`${policyKey}_remark`];
        delete newErrors[`${policyKey}_effectiveDate`];
        return newErrors;
      });
    }
  };

  const handleIsRevisedChange = (policyKey, revisedValue) => {
    setPolicies((prev) => {
      const previousPolicy = normalizePolicy(previousPoliciesState?.[policyKey]);
      return {
        ...prev,
        [policyKey]: {
          ...prev[policyKey],
          isRevised: revisedValue,
          ...(revisedValue
            ? {
                // If revised is Yes, user should provide new evidence/details.
                documentUrl: "",
                remark: "",
                effectiveDate: "",
              }
            : {
                // If revised is No, restore previous policy values.
                documentUrl: previousPolicy.documentUrl || "",
                remark: previousPolicy.remark || "",
                effectiveDate: previousPolicy.effectiveDate || "",
              }),
        },
      };
    });

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[policyKey];
      delete newErrors[`${policyKey}_remark`];
      delete newErrors[`${policyKey}_effectiveDate`];
      return newErrors;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    const { min: effMin, max: effMax } = getEffectiveDateMinMax(reportingYear);

    const shouldShowIsRevisedForPolicy = (policyKey) =>
      !isFirstEntry &&
      hasUploadedDocument(previousPoliciesState?.[policyKey]?.documentUrl);

    const addAnnualYesMandatoryErrors = (key, current) => {
      if (!hasUploadedDocument(current.documentUrl)) {
        newErrors[key] = "File upload is mandatory.";
      }
      if (!current.effectiveDate) {
        newErrors[`${key}_effectiveDate`] = "Effective date is mandatory.";
      } 
    };

    policyFields.forEach(({ key }) => {
      const current = policies[key] || {};
      if (moduleType !== "annual" || !current.status) return;

      const showIsRevised = shouldShowIsRevisedForPolicy(key);

      // Same as EShareholder: when "Is the Policy revised?" is not shown, Yes requires
      // evidence, remarks, and effective date (with reporting-year bounds).
      if (!showIsRevised) {
        addAnnualYesMandatoryErrors(key, current);
        return;
      }

      if (current.isRevised) {
        addAnnualYesMandatoryErrors(key, current);
      } 
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const isQuarterly = moduleType === "quarterly";
      const payload = isQuarterly
        ? {
            id,
            module: "quarterly",
           
            stepsCompleted: {
              policies: true,
            },
          }
        : {
            id,
            module: "annual",
            policies: policies,
            stepsCompleted: {
              policies: true,
            },
          };
      await put(`${api}/governance/updateGovernance`, payload);
      navigate("/governance/data");
    } catch (error) {
      console.error("Error submitting data:", error.response || error);
    }
  };

  const { min: effectiveDateMin, max: effectiveDateMax } =
    moduleType === "annual"
      ? getEffectiveDateMinMax(reportingYear)
      : { min: undefined, max: undefined };

  return (
    <MainDashboardLayout sidebar={<GovernanceLayout />}>
      <Box
        sx={{
          margin: "20px auto",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          minHeight: moduleType === "annual" ? "calc(100vh - 170px)" : "auto",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#1976d2",
          },
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bold",
            margin: "1.2% 0",
            textDecoration: "underline",
          }}
        >
          E. POLICIES
        </Typography>

        {policyFields.map(({ key: policyKey, label }, index) => (
          <>
            <Box key={policyKey} sx={{ marginBottom: 2, }}>
              <Typography sx={{ fontWeight: "bold" }}>
                {index + 1}. {label}
              </Typography>
              <RadioGroup
                row
                sx={{ml:"1.1rem"}}
                value={policies[policyKey].status ? "yes" : "no"}
                onChange={(e) =>
                  handlePolicyChange(
                    policyKey,
                    "status",
                    e.target.value === "yes"
                  )
                }
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                  label="Yes"
                />
                <FormControlLabel
                  value="no"
                  control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                  label="No"
                />
              </RadioGroup>
              {!policies[policyKey].status && (
                <Box sx={{ width: "55%" }}>
                  <TextField
                    fullWidth
                    value={policies[policyKey].remark}
                    onChange={(e) =>
                      handlePolicyChange(policyKey, "remark", e.target.value)
                    }
                    placeholder="Enter Additional Text"
                    variant="outlined"
                    error={!!errors[`${policyKey}_remark`]}
                    multiline
                    minRows={1}
                    InputProps={{
                      sx: {
                        padding: "9px 12px",
                      },
                    }}
                    sx={{
                      marginBottom: "0.25rem",
                      ml: "1.1rem",
                    }}
                  />
                  <FormHelperText
                    sx={{ color: "#D34040", fontSize: "14px", marginLeft: "0.1rem" }}
                  >
                    {errors[`${policyKey}_remark`]}
                  </FormHelperText>
                </Box>
              )}

              {!isFirstEntry && policies[policyKey].status &&
                hasUploadedDocument(previousPoliciesState?.[policyKey]?.documentUrl) && (
                <Box sx={{ ml: "1.1rem", mt: 1 }}>
                  <Typography sx={{ fontWeight: "bold", mb: "4px" }}>
                    Is the Policy revised?
                  </Typography>
                  <RadioGroup
                    row
                    value={policies[policyKey].isRevised ? "yes" : "no"}
                    onChange={(e) =>
                      handleIsRevisedChange(policyKey, e.target.value === "yes")
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio sx={{ "&.Mui-checked": { color: "#1c486b" } }} />}
                      label="No"
                    />
                  </RadioGroup>
                </Box>
              )}

              {policies[policyKey].status && (
                  <Box sx={{ ml: "0.9rem" }}>
                    <Typography sx={{ fontWeight: "bold", ml: "0.1rem", mb: 0.5 }}>
                      Upload evidence (for Policy):
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap:policies[policyKey].documentUrl?"16px": "40px", ml: "0.1rem" }}>
                      <Box sx={{ width:policies[policyKey].documentUrl ? "233px" : "100px" }}>
                        <FileUpload
                          setDocumentUrl={(url) =>
                            handlePolicyChange(policyKey, "documentUrl", url)
                          }
                          filesList={policies[policyKey].documentUrl}
                          title={""}
                          ml={0}
                          mt={0}
                          marginTop={0}
                          isDisabled={
                            !(!policies[policyKey].isRevised &&
                              !isFirstEntry &&
                              hasUploadedDocument(
                                previousPoliciesState?.[policyKey]?.documentUrl
                              )
                            )
                          }
                        />
                        {errors[policyKey] ? (
                          <FormHelperText
                            sx={{ color: "#D34040", fontSize: "14px", minHeight: "20px" }}
                          >
                            {errors[policyKey]}
                          </FormHelperText>
                        ) : null}
                      </Box>

                      <Box sx={{ width: "30%",marginTop: policies[policyKey].documentUrl ? "15px" : "10px" }}>
                        <TextField
                          fullWidth
                          value={policies[policyKey].remark}
                          onChange={(e) =>
                            handlePolicyChange(policyKey, "remark", e.target.value)
                          }
                          placeholder="Enter Additional Text"
                          variant="outlined"
                          error={!!errors[`${policyKey}_remark`]}
                          multiline
                          minRows={1}
                          InputProps={{
                            sx: {
                              padding: "9px 12px",
                            },
                          }}
                        />
                        <FormHelperText
                          sx={{ color: "#D34040", fontSize: "14px", minHeight: "20px" }}
                        >
                          {errors[`${policyKey}_remark`] || " "}
                        </FormHelperText>
                      </Box>
                    </Box>

                    <Typography sx={{ fontWeight: "bold", ml: "0.1rem",mb:"12px" }}>
                      Effective date (for Policy):
                    </Typography>
                    <TextField
                      type="date"
                      fullWidth
                      value={policies[policyKey].effectiveDate || ""}
                      onChange={(e) =>
                        handlePolicyChange(policyKey, "effectiveDate", e.target.value)
                      }
                      error={!!errors[`${policyKey}_effectiveDate`]}
                      inputProps={{
                        min: "2021-01-01",
                        ...(effectiveDateMax ? { max: effectiveDateMax } : {}),
                      }}
                      sx={{
                        marginBottom: "0.25rem",
                        width: "30%",
                        ml: "0.1rem",        
                          "& .MuiInputBase-root": {
                              height: 40, }
                      }}
                    />
                    <FormHelperText
                      sx={{ color: "#D34040", fontSize: "14px", marginLeft: "0.1rem" ,width: "30%" }}
                    >
                      {errors[`${policyKey}_effectiveDate`]}
                    </FormHelperText>
                  </Box>
                )}
            </Box>
          </>
        ))}

        <ActionButtons
          Back={() =>
            navigate(`/governance/data/transparancy-disclosure/${id}`)
          }
          Move={handleSubmit}
          buttonText="Submit"
        />
      </Box>
    </MainDashboardLayout>
  );
};

export default Policies;
